import { Accordion, CheckBox, Message } from '@iwoca/orion';
import { Trans, useLingui } from '@lingui/react/macro';
import { clsx } from 'clsx';
import { useFormContext } from 'react-hook-form';

import styles from './PrivacyPolicyField.module.css';
import { TPersonalDetailsDataDE } from '../../PersonalDetailsDE';

const PrivacyPolicyField = () => {
  const { t } = useLingui();

  const {
    register,
    formState: { errors },
  } = useFormContext<TPersonalDetailsDataDE>();

  return (
    <div>
      <p className={styles.privacyPolicySection}>
        <Trans>
          I hereby confirm, on behalf of myself and any other managing
          directors, that I have read iwoca’s{' '}
          <a
            href="https://www.iwoca.de/compliance/allgemeine-geschaeftsbedingungen"
            target="_blank"
            rel="noreferrer"
          >
            Terms and Conditions
          </a>
          , the{' '}
          <a
            href="https://www.iwoca.de/compliance/datenschutzhinweis"
            target="_blank"
            rel="noreferrer"
          >
            Privacy Notice
          </a>{' '}
          and the{' '}
          <a
            href="https://www.iwoca.de/compliance/schufa-creditreform-hinweise"
            target="_blank"
            rel="noreferrer"
          >
            Notice on data transfer to Schufa Holding AG
          </a>{' '}
          and expressly agree to them. I also release iwoca Deutschland GmbH, as
          well as iwoca Management S.à r.l. acting on behalf of iwoca SCSp
          SICAV-SIF, from banking secrecy in order to enable data exchange and
          the processing of the credit application.
        </Trans>
      </p>
      <Accordion
        className={clsx(
          styles.privacyPolicyAccordionContainer,
          styles.privacyPolicyAccordion,
        )}
        titleClassName={styles.privacyPolicyAccordionTitle}
        title={t`Why do we need your consent?`}
        frame={false}
        small
      >
        <Accordion
          className={styles.privacyPolicyAccordion}
          title={t`How do we handle your data?`}
          frame={false}
          small
        >
          <p>
            <Trans>
              Data security is a top priority at iwoca. We only share your data
              with close partners when it is necessary to process your credit
              application. We will never pass your data to third parties that
              are not directly connected with your application. You can find
              more information{' '}
              <a
                href="https://www.iwoca.de/compliance/datenschutzhinweis"
                target="_blank"
                rel="noreferrer"
              >
                here
              </a>
              .
            </Trans>
          </p>
        </Accordion>
        <Accordion
          className={styles.privacyPolicyAccordion}
          title={t`Why do we collect your Schufa information?`}
          frame={false}
          small
        >
          <p>
            <Trans>
              The Schufa report helps us speed up our credit analysis. Your
              creditworthiness does feed into our analysis, but it is only
              decisive when there are hard negative markers. In plain terms: a
              disputed phone bill from 2016 is not a problem, whereas we do need
              to look more closely at issues repaying earlier loans. Don’t
              worry: 90% of German Schufa files contain only positive entries.
            </Trans>
          </p>
        </Accordion>
        <Accordion
          className={styles.privacyPolicyAccordion}
          title={t`Will my Schufa entry get worse as a result of the information?`}
          frame={false}
          small
        >
          <p>
            <Trans>
              No, our Schufa query has no effect on your report. We only make a
              “score-neutral” conditions enquiry, which is not visible to other
              companies.
            </Trans>
          </p>
        </Accordion>
        <Accordion
          className={styles.privacyPolicyAccordion}
          title={t`Why do we need the consent of all managing directors?`}
          frame={false}
          small
        >
          <p>
            <Trans>
              We usually need the consent of all further managing directors, as
              our credit analysis also checks their personal creditworthiness.
              To keep the process straightforward and handle your credit
              application as quickly as possible, we ask you to obtain your
              colleagues’ consent. If other managing directors do not agree,
              please feel free to get in touch with us.
            </Trans>
          </p>
        </Accordion>
      </Accordion>
      <p className={styles.privacyPolicySection}>
        <Trans>
          If you have further questions about how your data is processed, or
          want to learn more about Schufa and the like, simply send us an email
          at{' '}
          <a href="mailto:support@iwoca.de" target="_blank" rel="noreferrer">
            support@iwoca.de
          </a>{' '}
          or call us on{' '}
          <a href="tel:06994323020" target="_blank" rel="noreferrer">
            069 9432 3020
          </a>
          .
        </Trans>
      </p>
      <CheckBox
        wrapperClassName={styles.privacyPolicyCheckboxContainer}
        className={clsx(styles.privacyPolicyCheckbox, {
          [styles.privacyPolicyCheckboxError]:
            errors.privacyPolicyAgreement?.message,
        })}
        labelProps={{ className: styles.privacyPolicyLabel }}
        {...register('privacyPolicyAgreement')}
        label={t`I accept the terms and conditions and acknowledge the privacy and SCHUFA notices`}
      />
      {errors.privacyPolicyAgreement?.message && (
        <Message
          className={styles.privacyPolicyError}
          message={errors.privacyPolicyAgreement?.message}
          type="error"
        />
      )}
    </div>
  );
};

export default PrivacyPolicyField;
